// ---------------------------------------------------------------------------
// SuggestionChips — context-aware suggestion pills rendered above composer.
// Clicking a chip sends it as the next user message via assistant-ui runtime.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { useThreadRuntime } from '@assistant-ui/react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { handleProjectCreateError } from '@/store/project-create-error'
import { useStudioLocale } from '@/i18n'
import { isAppCreationPrompt } from '../services/app-creation-prompt'
import {
  StudioGenerationBusyError,
  startAppGenerationFromPrompt,
} from '../services/start-app-generation-from-prompt'
import { surfaceStudioGenerationFailure } from '../services/studio-generation-failure-surface'
import { presentGenerationReadinessInChat } from '../services/present-generation-readiness'
import {
  buildUpdateExistingWorkPrompt,
  isUpdateExistingWorkChip,
} from '../services/update-existing-work-intent'

/** Derive contextual suggestion chips before a project is open. */
function getChips(hasProject: boolean, suggestions: string[]): string[] {
  if (hasProject) return []
  return suggestions
}

/**
 * User-perspective audit (production, 2026-08-04): clicking "Update existing
 * work" on a DASHBOARD route made the assistant ask "which app are you
 * working on?" while the page beside it listed the user's recent work and
 * the chat header said "Selected target: Current workspace". The capability
 * exists — inside a project workspace the same chat already carries GOAL and
 * target — only the dashboard was context-blind.
 *
 * The chip answers that question with the workspace's own read
 * (`userProjects`, the same store slice Home renders) instead of asking it.
 *
 * Both helpers now live in `services/update-existing-work-intent`, so the
 * chip's message and the deterministic intent that ACTS on it resolve the
 * same workspace read through the same de-duplication — the choice the user
 * is offered and the project that gets opened cannot drift apart. Re-exported
 * here because this module has been their import site since PR #2020.
 */
export {
  buildUpdateExistingWorkPrompt,
  isUpdateExistingWorkChip,
} from '../services/update-existing-work-intent'

export function SuggestionChips() {
  const { strings, locale } = useStudioLocale()
  const runtime = useThreadRuntime()
  const currentProject = useStudioStore((s) => s.currentProject)
  const userProjects = useStudioStore((s) => s.userProjects)
  const [isStartingApp, setIsStartingApp] = useState(false)

  const hasProject = !!currentProject

  const chips = getChips(hasProject, strings.chat.suggestions)

  if (chips.length === 0) return null

  const handleClick = (text: string) => {
    if (isAppCreationPrompt(text)) {
      if (isStartingApp) return
      // Task 5.5 — presentation first (spec §3): the chip submit renders
      // the readiness panel in the thread and does NOT start generation;
      // the block below (this chip's pre-existing start block, verbatim)
      // runs only on the panel's generate click.
      setIsStartingApp(true)
      void presentGenerationReadinessInChat({
        prompt: text,
        proceed: (confirmedPrompt) => {
          setIsStartingApp(true)
          useStudioStore.getState().setPendingChatNotice(
            strings.chat.appGenerationStarting,
          )
          void startAppGenerationFromPrompt(confirmedPrompt, {
            onGenerationError: (message) => toast.error(message),
          })
            .then(async (started) => {
              if (started.status === 'clarification-required') return
              // Task 4 — the producer already wrote a chat notice for this
              // status; nothing started, so there is no `.generation` to await.
              if (started.status === 'generation-readiness-blocked') return
              await started.generation
            })
            .catch((error) => {
              if (error instanceof StudioGenerationBusyError) {
                useStudioStore
                  .getState()
                  .setPendingChatNotice(error.userMessage)
                return
              }
              // Same silent-failure class as the composer: this used to leave the
              // "starting…" notice stale and rely on an auto-dismissing toast, so a
              // typed fail-closed vanished from the screen within seconds.
              surfaceStudioGenerationFailure({ error, intent: text, locale })
              handleProjectCreateError(error, {
                toastError: () => {},
                signOut: () => void useStudioStore.getState().signOut(),
              })
            })
            .finally(() => setIsStartingApp(false))
        },
      }).finally(() => setIsStartingApp(false))
      return
    }

    runtime.append({
      role: 'user',
      content: [
        {
          type: 'text',
          text: isUpdateExistingWorkChip(text)
            ? buildUpdateExistingWorkPrompt(text, userProjects)
            : text,
        },
      ],
    })
  }

  return (
    <div className="flex flex-wrap gap-1.5 px-4">
      {chips.map((chip, index) => (
        <button
          key={chip}
          type="button"
          onClick={() => handleClick(chip)}
          disabled={isStartingApp}
          aria-busy={isStartingApp && index === 0}
          data-testid="chat-suggestion-chip"
          className={[
            'max-w-full rounded-md border px-2 py-1 text-left text-[11px] font-medium transition-colors disabled:cursor-wait disabled:opacity-60',
            index === 0
              ? 'border-primary/25 bg-primary/10 text-primary hover:bg-primary/15'
              : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
          ].join(' ')}
        >
          {chip}
        </button>
      ))}
    </div>
  )
}
